import { useEffect, useState } from 'react';
import JobTabs, { JobTag } from '../components/JobTabs.js';
import { TagList } from '../components/TagChips.js';
import { STOCK_TAGS } from '../lib/projectJobs.js';
import { listMoments, listPeople } from '../lifeos/api.js';
import { birthdayLabel, daysLeftLabel, daysToBirthday, humanize, plural, since, ymdInHomeZone } from '../lifeos/format.js';
import type { PersonListItem } from '../lifeos/types.js';
import { sphereColor, useSpheres } from '../lifeos/spheres.js';
import { useLoad } from '../lifeos/useLoad.js';
import { Chip, EmptyState, Group, Icon, Loader, Page, Row, Avatar, SearchField, useParamSetter, When } from '../ui/index.js';

/** Rows of "Everyone" shown before "Show all N" (the list is the whole address book). */
export const EVERYONE_SHOWN = 30;

const LETTERS = [...'ABCDEFGHIJKLMNOPQRSTUVWXYZ', '#'];

/** 'Dana Ruiz' → 'D'; anything not A–Z files under '#'. */
export function letterOf(name: string): string {
  const c = name.trim().charAt(0).toUpperCase();
  return c >= 'A' && c <= 'Z' ? c : '#';
}

/** Name, contact name and tags, lower-cased, for the as-you-type filter. */
function haystack(p: PersonListItem): string {
  return [p.name, p.relationship ?? '', ...(p.tags ?? [])].join(' ').toLowerCase();
}

/**
 * People (`/people`): a search box that filters as you type, the job tabs and
 * tag chips, then Inner circle, Clients, and Everyone A–Z under sticky letter
 * headers (first 30, then "Show all N"; a jump bar on desktop). A filter or a
 * search shows every match in one A–Z list. `?org=` comes from an org link.
 */
export default function People(): JSX.Element {
  const [params, setParam] = useParamSetter();
  const q = params.get('q') ?? '';
  const sphere = params.get('sphere');
  const org = params.get('org');
  const month = params.get('birthdayMonth');
  const tag = params.get('tag');
  const [search, setSearch] = useState(q);
  const [showAll, setShowAll] = useState(false);
  const [jump, setJump] = useState<string | null>(null);
  const spheres = useSpheres();
  // The address book is small: load it once per job/org/month and filter names in the browser.
  const people = useLoad(
    () =>
      listPeople({
        sphere: sphere ?? undefined,
        org: org ?? undefined,
        birthdayMonth: month ? Number(month) : undefined,
      }),
    [sphere, org, month],
  );
  const moments = useLoad(() => listMoments(), []);
  const today = ymdInHomeZone(new Date());
  const thisMonth = String(Number(today.slice(5, 7)));
  const needle = search.trim().toLowerCase();

  // Keep ?q= in the URL (back button, shared links) without refetching.
  useEffect(() => {
    const t = setTimeout(() => {
      if (search.trim() !== q) setParam('q', search.trim() || null);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // A letter tapped while the list was folded scrolls once the full list is drawn.
  useEffect(() => {
    if (!jump) return;
    document.getElementById(`ppl-${jump}`)?.scrollIntoView?.({ block: 'start' });
    setJump(null);
  }, [jump, showAll]);

  return (
    <Page title="People" sub={people.data ? `${people.data.length} ${people.data.length === 1 ? 'person' : 'people'}` : undefined}>
      <SearchField value={search} onChange={setSearch} placeholder="Search people" label="Search people" />
      <JobTabs spheres={spheres} value={sphere} onChange={(k) => setParam('sphere', k)} />
      <div className="ui-chips" role="group" aria-label="Filters">
        <Chip on={month === thisMonth} onClick={() => setParam('birthdayMonth', month === thisMonth ? null : thisMonth)}>
          Birthdays this month
        </Chip>
        {org && (
          <Chip on onClick={() => setParam('org', null)} label="Clear organisation">
            One organisation
            <Icon name="x" size={14} />
          </Chip>
        )}
      </div>
      <TagFilter
        value={tag}
        tags={[...new Set([...STOCK_TAGS, ...(people.data ?? []).flatMap((p) => p.tags ?? [])])]}
        onChange={(t) => setParam('tag', t)}
      />
      <Loader state={people}>
        {(all) => {
          const filtered = all.filter((p) => (!tag || (p.tags ?? []).includes(tag)) && (!needle || haystack(p).includes(needle)));
          if (filtered.length === 0)
            return <EmptyState icon="users" title={needle ? `Nobody matches “${search.trim()}”.` : tag ? `Nobody tagged ${humanize(tag)}.` : 'Nobody matches.'} />;
          const now = new Date();
          const narrowed = !!(needle || org || month || tag);
          // Mo's Rule: the people who matter most, each with their next date that matters.
          const circle = narrowed ? [] : filtered.filter((p) => p.importance >= 3);
          const clients = narrowed ? [] : filtered.filter((p) => (p.tags ?? []).includes('client') || (!(p.tags?.length) && p.relationship === 'client'));
          const nextMoment = (id: number) =>
            (moments.data ?? [])
              .filter((m) => m.contactId === id)
              .map((m) => ({ m, n: daysToBirthday(m.date, today) }))
              .sort((a, b) => a.n - b.n)[0];
          const sorted = month
            ? [...filtered].sort((a, b) => (a.birthday?.day ?? 0) - (b.birthday?.day ?? 0))
            : [...filtered].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
          // Searching or filtering shows every match; the bare directory folds after 30.
          const folded = !narrowed && !showAll && sorted.length > EVERYONE_SHOWN;
          const shown = folded ? sorted.slice(0, EVERYONE_SHOWN) : sorted;
          const byLetter = new Map<string, PersonListItem[]>();
          for (const p of shown) {
            const l = month ? '' : letterOf(p.name);
            byLetter.set(l, [...(byLetter.get(l) ?? []), p]);
          }
          const present = new Set(sorted.map((p) => letterOf(p.name)));
          const row = (p: PersonListItem): JSX.Element => (
            <Row
              key={p.id}
              className="la-person ppl-row"
              to={`/people/${p.id}`}
              title={
                <>
                  {p.name}
                  {p.importance >= 3 && (
                    <span className="la-star" aria-label="important">
                      {' '}
                      ★
                    </span>
                  )}
                  {p.sphereKey && <> <JobTag sphere={spheres.find((s) => s.key === p.sphereKey)} /></>}
                </>
              }
              sub={
                (p.tags?.length ?? 0) > 0 || (p.birthday && (month || daysToBirthday(p.birthday, today) <= 30)) ? (
                  <>
                    <TagList tags={p.tags} />
                    {/* A birthday this month gets its own line, so the name never wraps for it. */}
                    {p.birthday && (month || daysToBirthday(p.birthday, today) <= 30) && (
                      <span className="la-line">
                        <When tone={daysToBirthday(p.birthday, today) <= 7 ? 'today' : 'later'}>{birthdayLabel(p.birthday, today)}</When>
                      </span>
                    )}
                  </>
                ) : undefined
              }
            />
          );
          return (
            <>
              <div className="ppl-top">
              <Group header="Inner circle" footer="Mo's Rule: the people who matter most, and the dates to reach out on.">
                {circle.length === 0 && !narrowed && (
                  <Row tone="muted" chevron={false} title="Nobody yet — open someone, Edit, and set Importance to Inner circle" />
                )}
                {circle.map((p) => {
                  const next = nextMoment(p.id);
                  return (
                    <Row
                      key={p.id}
                      to={`/people/${p.id}`}
                      leading={<Avatar name={p.name} color={sphereColor(spheres, p.sphereKey)} />}
                      title={p.name}
                      sub={next ? `${next.m.label} · ${daysLeftLabel(next.n)}` : 'No dates yet — add one'}
                      detail={next && next.n <= 7 ? <When tone="today">{daysLeftLabel(next.n)}</When> : undefined}
                    />
                  );
                })}
              </Group>

              {clients.length > 0 && (
                <Group header={`Clients · ${clients.length}`} label="Clients">
                  {clients.map((p) => (
                    <Row
                      key={p.id}
                      className="ppl-client"
                      to={`/people/${p.id}`}
                      leading={<Avatar name={p.name} color={sphereColor(spheres, p.sphereKey)} />}
                      title={p.name}
                      sub={p.lastContactAt ? `talked ${since(p.lastContactAt, now)}` : undefined}
                    />
                  ))}
                </Group>
              )}
              </div>

              <section className="ppl-all" aria-label="People">
                <div className="ppl-all__head">
                  <h2 className="ui-group__title">{narrowed ? `${plural(sorted.length, 'match', 'matches')}` : `Everyone · ${sorted.length}`}</h2>
                </div>
                {!month && sorted.length > EVERYONE_SHOWN && (
                  <nav className="ppl-jump" aria-label="Jump to letter">
                    {LETTERS.map((l) => (
                      <button
                        key={l}
                        type="button"
                        disabled={!present.has(l)}
                        onClick={() => {
                          setShowAll(true);
                          setJump(l);
                        }}
                      >
                        {l}
                      </button>
                    ))}
                  </nav>
                )}
                {[...byLetter.entries()].map(([l, rows]) => (
                  <div key={l || 'all'} className="ppl-letter">
                    {l && (
                      <h3 className="ppl-letter__head" id={`ppl-${l}`}>
                        {l}
                      </h3>
                    )}
                    <ul className="ui-group ppl-list">{rows.map(row)}</ul>
                  </div>
                ))}
                {folded && (
                  <button type="button" className="ppl-more" onClick={() => setShowAll(true)}>
                    Show all {sorted.length}
                  </button>
                )}
              </section>
            </>
          );
        }}
      </Loader>
    </Page>
  );
}

/** One row of tag chips: All plus each tag (plural, "Friends"); tapping the lit one clears it. */
function TagFilter({ value, tags, onChange }: { value: string | null; tags: readonly string[]; onChange: (t: string | null) => void }): JSX.Element {
  const plural = (t: string): string => {
    const h = humanize(t);
    return t === 'family' ? h : /s$/.test(h) ? h : `${h}s`;
  };
  return (
    <div className="ui-chips people__tags" role="group" aria-label="Tag">
      <Chip on={value === null} onClick={() => onChange(null)}>
        Everyone
      </Chip>
      {tags.map((t) => (
        <Chip key={t} on={value === t} onClick={() => onChange(value === t ? null : t)}>
          {plural(t)}
        </Chip>
      ))}
    </div>
  );
}
